import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button, Tag } from 'antd';
import { toast } from '../../../utils/toast.js';
import {
  CheckSquareOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  CheckOutlined,
  SwapOutlined,
  BranchesOutlined,
  FilterOutlined,
  MessageOutlined,
} from '@ant-design/icons';

// Hash nhẹ tạo key lưu trữ cho từng bài/tài liệu
function simpleHash(str) {
  if (!str) return 'lms_empty';
  let hash = 0;
  for (let i = 0; i < Math.min(str.length, 500); i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return 'lms_interactive_' + Math.abs(hash);
}

// Bảng màu pastel nhẹ nhàng, dịu mắt
const PASTEL_STEP_COLORS = [
  { bg: '#e0f2fe', text: '#0284c7', border: '#bae6fd' }, // Pastel Sky Blue
  { bg: '#ede9fe', text: '#7c3aed', border: '#ddd6fe' }, // Pastel Purple
  { bg: '#cffafe', text: '#0891b2', border: '#a5f3fc' }, // Pastel Cyan
  { bg: '#dcfce7', text: '#16a34a', border: '#bbf7d0' }, // Pastel Mint Green
  { bg: '#fef3c7', text: '#d97706', border: '#fde68a' }, // Pastel Amber
  { bg: '#fce7f3', text: '#db2777', border: '#fbcfe8' }, // Pastel Pink
];

// Widget 1: Ô nhập ngày tháng (cho bài tập thực hành)
function InteractiveDateInput({ value, onChange, isOrange }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 8px',
        background: isOrange ? '#fffbeb' : '#f8fafc',
        border: isOrange ? '1.5px solid #fde68a' : '1px solid #cbd5e1',
        borderRadius: 6,
        margin: '0 4px',
        verticalAlign: 'middle',
      }}
    >
      <span style={{ fontSize: 13 }}>📅</span>
      <input
        type="text"
        placeholder="DD/MM/YYYY"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: 95,
          border: 'none',
          background: 'transparent',
          fontSize: 13,
          fontWeight: 700,
          color: isOrange ? '#b45309' : '#0f172a',
          outline: 'none',
          textAlign: 'center',
        }}
      />
    </span>
  );
}

// Widget 2: Ô nhập số liệu / chỉ số có đơn vị (cho bài tập thực hành)
function InteractiveMetricInput({ value, unit, onChange }) {
  const isScore = unit.startsWith('/');
  const width = isScore ? 42 : unit.length > 5 ? 75 : 55;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        margin: '0 3px',
        verticalAlign: 'baseline',
      }}
    >
      <input
        type="text"
        placeholder="___"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width,
          padding: '2px 6px',
          border: 'none',
          borderBottom: '2px solid #0284c7',
          background: '#f1f5f9',
          borderRadius: '4px 4px 0 0',
          fontSize: 13,
          fontWeight: 700,
          color: '#0369a1',
          textAlign: 'center',
          outline: 'none',
        }}
      />
      <span style={{ fontWeight: 600, color: '#334155', fontSize: 13 }}>{unit}</span>
    </span>
  );
}

// Widget 3: Nút bấm lựa chọn phương án dạng Pill (cho bài tập thực hành)
function InteractiveChoiceGroup({ options, selectedValue, onSelect }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        flexWrap: 'wrap',
        gap: 6,
        margin: '2px 4px',
        verticalAlign: 'middle',
      }}
    >
      {options.map((opt, i) => {
        const isSelected = selectedValue === opt;
        const lower = opt.toLowerCase();
        const isNegative =
          lower.includes('không') ||
          lower.includes('lệch') ||
          lower.includes('chậm') ||
          lower.includes('chưa');
        const activeColor = isNegative ? '#dc2626' : '#16a34a';
        const activeBg = isNegative ? '#fef2f2' : '#f0fdf4';

        return (
          <button
            key={i}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(isSelected ? null : opt);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '3px 10px',
              borderRadius: 14,
              border: isSelected ? `1.5px solid ${activeColor}` : '1px solid #cbd5e1',
              background: isSelected ? activeBg : '#ffffff',
              color: isSelected ? activeColor : '#475569',
              fontWeight: isSelected ? 700 : 500,
              fontSize: 12,
              cursor: 'pointer',
              boxShadow: isSelected ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s ease',
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                border: isSelected ? `3px solid ${activeColor}` : '1.5px solid #94a3b8',
                background: isSelected ? '#ffffff' : 'transparent',
                display: 'inline-block',
              }}
            />
            {opt}
          </button>
        );
      })}
    </span>
  );
}

// Widget 4: Ô vuông đánh dấu tiêu chí (cho bài tập thực hành)
function InteractiveCheckboxToggle({ isChecked, onToggle }) {
  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 22,
        borderRadius: 5,
        border: isChecked ? '2px solid #16a34a' : '2px solid #cbd5e1',
        background: isChecked ? '#16a34a' : '#ffffff',
        color: '#ffffff',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 800,
        margin: '0 4px',
        verticalAlign: 'middle',
        userSelect: 'none',
        boxShadow: isChecked ? '0 2px 5px rgba(22, 163, 74, 0.25)' : 'none',
        transition: 'all 0.15s ease',
      }}
      title="Bấm để đánh dấu hoàn thành"
    >
      {isChecked ? '✓' : ''}
    </span>
  );
}

// Widget 5: Sơ đồ phễu bán hàng tại quầy Pastel
function VisualFunnelFlow({ root, nodes, rawText }) {
  const [copied, setCopied] = useState(false);

  return (
    <div
      style={{
        margin: '22px 0',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        overflow: 'hidden',
      }}
    >
      {/* Header Pastel Blue */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '11px 18px',
          background: '#f0f9ff',
          borderBottom: '1px solid #bae6fd',
          color: '#0369a1',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FilterOutlined style={{ fontSize: 16, color: '#0284c7' }} />
          <span style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.3, color: '#0369a1' }}>
            SƠ ĐỒ PHỄU TẠI QUẦY ({nodes.length} NẤC CHUYỂN ĐỔI)
          </span>
        </div>
        <Button
          size="small"
          type="text"
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={() => {
            navigator.clipboard.writeText(rawText);
            setCopied(true);
            toast.success('Đã sao chép sơ đồ phễu');
            setTimeout(() => setCopied(false), 2000);
          }}
          style={{ color: '#0284c7', fontSize: 12, fontWeight: 600 }}
        >
          {copied ? 'Đã chép' : 'Sao chép'}
        </Button>
      </div>

      <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Root Node Pastel */}
        {root && (
          <div
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              color: '#334155',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            <span>👥 {root}</span>
            <span
              style={{
                padding: '2px 8px',
                borderRadius: 10,
                background: '#e0f2fe',
                color: '#0284c7',
                fontSize: 11,
                fontWeight: 700,
                border: '1px solid #bae6fd',
              }}
            >
              Đầu vào phễu (100%)
            </span>
          </div>
        )}

        {/* Funnel Steps Pastel */}
        {nodes.map((step, idx) => {
          const colorTheme = PASTEL_STEP_COLORS[idx % PASTEL_STEP_COLORS.length];

          return (
            <div
              key={step.stepNo}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '11px 16px',
                borderRadius: 8,
                background: '#ffffff',
                border: '1px solid #f1f5f9',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 7,
                    background: colorTheme.bg,
                    color: colorTheme.text,
                    border: `1px solid ${colorTheme.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {step.stepNo}
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                  {step.title}
                </span>
              </div>

              {step.desc && (
                <span
                  style={{
                    padding: '3px 10px',
                    borderRadius: 12,
                    background: step.desc.includes('mất') ? '#fef3c7' : '#f0f9ff',
                    color: step.desc.includes('mất') ? '#b45309' : '#0369a1',
                    border: `1px solid ${step.desc.includes('mất') ? '#fde68a' : '#bae6fd'}`,
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  {step.desc}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Widget 6: Sơ đồ quy trình trực quan tuần tự Pastel
function VisualProcessFlow({ steps, rawText }) {
  const [copied, setCopied] = useState(false);

  return (
    <div
      style={{
        margin: '22px 0',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        overflow: 'hidden',
      }}
    >
      {/* Header Pastel Purple */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '11px 18px',
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          color: '#1e293b',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BranchesOutlined style={{ fontSize: 16, color: '#7c3aed' }} />
          <span style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.3, color: '#5b21b6' }}>
            SƠ ĐỒ QUY TRÌNH THỰC HIỆN ({steps.filter((s) => !s.isConnector).length} BƯỚC CỐT LÕI)
          </span>
        </div>
        <Button
          size="small"
          type="text"
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={() => {
            navigator.clipboard.writeText(rawText);
            setCopied(true);
            toast.success('Đã sao chép sơ đồ quy trình');
            setTimeout(() => setCopied(false), 2000);
          }}
          style={{ color: '#7c3aed', fontSize: 12, fontWeight: 600 }}
        >
          {copied ? 'Đã chép' : 'Sao chép'}
        </Button>
      </div>

      {/* Steps List */}
      <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {steps.map((step, idx) => {
          if (step.isConnector) {
            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  margin: '-3px 0',
                  color: '#7c3aed',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                <SwapOutlined rotate={90} style={{ fontSize: 14 }} />
                <span
                  style={{
                    background: '#f5f3ff',
                    border: '1px solid #ddd6fe',
                    color: '#6d28d9',
                    padding: '2px 10px',
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {step.text || 'phải khớp đồng bộ'}
                </span>
              </div>
            );
          }

          const colorTheme = PASTEL_STEP_COLORS[idx % PASTEL_STEP_COLORS.length];

          return (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '12px 16px',
                borderRadius: 8,
                background: '#ffffff',
                border: '1px solid #f1f5f9',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              }}
            >
              {/* Number Badge */}
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: colorTheme.bg,
                  color: colorTheme.text,
                  border: `1px solid ${colorTheme.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {step.num || idx + 1}
              </div>

              {/* Step Content */}
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: '#1e293b',
                    marginBottom: step.desc ? 2 : 0,
                  }}
                >
                  {step.title}
                </div>
                {step.desc && (
                  <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.5 }}>
                    {step.desc}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Widget 7: Công thức ghép nối Pastel Mint Green
function VisualFormulaFlow({ formulaParts, rawText }) {
  const [copied, setCopied] = useState(false);

  return (
    <div
      style={{
        margin: '18px 0',
        padding: '14px 18px',
        borderRadius: 10,
        background: '#f0fdf4',
        border: '1px solid #bbf7d0',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: '#166534',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          🧩 CÔNG THỨC & CẤU TRÚC GHÉP NỐI
        </span>
        <Button
          size="small"
          type="text"
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={() => {
            navigator.clipboard.writeText(rawText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          style={{ fontSize: 11, color: '#166534' }}
        >
          {copied ? 'Đã chép' : 'Sao chép'}
        </Button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        {formulaParts.map((part, i) => (
          <React.Fragment key={i}>
            <div
              style={{
                padding: '6px 14px',
                background: '#ffffff',
                border: '1px solid #86efac',
                borderRadius: 6,
                fontWeight: 700,
                fontSize: 12.5,
                color: '#15803d',
                boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
              }}
            >
              {part}
            </div>
            {i < formulaParts.length - 1 && (
              <span style={{ fontSize: 16, fontWeight: 900, color: '#16a34a' }}>+</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// Widget 8: Khung mẫu kịch bản / Code block nền pastel sáng nhẹ nhàng
function VisualPromptCard({ rawText }) {
  const [copied, setCopied] = useState(false);

  return (
    <div
      style={{
        margin: '18px 0',
        borderRadius: 10,
        border: '1px solid #e2e8f0',
        background: '#f8fafc',
        color: '#1e293b',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px',
          background: '#f1f5f9',
          borderBottom: '1px solid #e2e8f0',
          fontSize: 12,
        }}
      >
        <span style={{ color: '#475569', fontWeight: 600 }}>Sơ đồ / Kịch bản thực hành</span>
        <Button
          size="small"
          type="text"
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={() => {
            navigator.clipboard.writeText(rawText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          style={{ color: '#0284c7', fontSize: 12, fontWeight: 600 }}
        >
          {copied ? 'Đã sao chép' : 'Sao chép'}
        </Button>
      </div>
      <div
        style={{
          padding: '14px 16px',
          fontFamily: 'Consolas, Monaco, monospace',
          fontSize: 12.5,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: '#334155',
        }}
      >
        {rawText}
      </div>
    </div>
  );
}

// Widget 9: Kịch bản hội thoại mẫu (Roleplay Chat)
function VisualChatFlow({ messages, rawText }) {
  const [copied, setCopied] = useState(false);

  return (
    <div
      style={{
        margin: '22px 0',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '11px 18px',
          background: '#f0fdf4',
          borderBottom: '1px solid #bbf7d0',
          color: '#166534',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MessageOutlined style={{ fontSize: 16, color: '#16a34a' }} />
          <span style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.3, color: '#166534' }}>
            💬 KỊCH BẢN HỘI THOẠI MẪU TẠI QUẦY (ROLEPLAY THỰC CHIẾN)
          </span>
        </div>
        <Button
          size="small"
          type="text"
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={() => {
            navigator.clipboard.writeText(rawText);
            setCopied(true);
            toast.success('Đã sao chép kịch bản hội thoại');
            setTimeout(() => setCopied(false), 2000);
          }}
          style={{ color: '#166534', fontSize: 12, fontWeight: 600 }}
        >
          {copied ? 'Đã chép' : 'Sao chép'}
        </Button>
      </div>

      <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: 14, background: '#f8fafc' }}>
        {messages.map((msg, idx) => {
          const lower = msg.speaker.toLowerCase();
          const isPharmacist = lower.includes('dược sĩ') || lower.includes('ds') || lower.includes('nhân viên') || lower.includes('bạn');
          const isNote = lower.includes('lưu ý') || lower.includes('ghi chú') || lower.includes('mục tiêu') || lower.includes('phân tích');

          if (isNote) {
            return (
              <div
                key={idx}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  background: '#fffbeb',
                  border: '1px dashed #fde68a',
                  color: '#92400e',
                  fontSize: 12.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span>💡</span>
                <span><strong>{msg.speaker}:</strong> {msg.text}</span>
              </div>
            );
          }

          return (
            <div
              key={idx}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isPharmacist ? 'flex-end' : 'flex-start',
                maxWidth: '100%',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 13 }}>{isPharmacist ? '👨‍⚕️' : '👤'}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: isPharmacist ? '#166534' : '#334155' }}>
                  {msg.speaker} {msg.tag && <Tag color={isPharmacist ? 'green' : 'blue'} style={{ fontSize: 10, marginLeft: 4 }}>{msg.tag}</Tag>}
                </span>
              </div>
              <div
                style={{
                  maxWidth: '85%',
                  padding: '10px 15px',
                  borderRadius: isPharmacist ? '14px 4px 14px 14px' : '4px 14px 14px 14px',
                  background: '#ffffff',
                  border: isPharmacist ? '1.5px solid #86efac' : '1.5px solid #cbd5e1',
                  color: '#1e293b',
                  fontSize: 13.5,
                  lineHeight: 1.55,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                }}
              >
                {msg.text}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Widget 10: Thẻ So Sánh ĐÚNG vs SAI (Do's & Don'ts)
function VisualDosDontsFlow({ dos, donts, rawText }) {
  const [copied, setCopied] = useState(false);

  return (
    <div
      style={{
        margin: '22px 0',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '11px 18px',
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <span style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>
          ⚖️ NGUYÊN TẮC THỰC CHIẾN TẠI QUẦY: NÊN LÀM vs TUYỆT ĐỐI TRÁNH
        </span>
        <Button
          size="small"
          type="text"
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={() => {
            navigator.clipboard.writeText(rawText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          style={{ fontSize: 11, color: '#64748b' }}
        >
          {copied ? 'Đã chép' : 'Sao chép'}
        </Button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
          gap: 16,
          padding: '18px',
        }}
      >
        {/* DO column */}
        <div
          style={{
            background: '#f0fdf4',
            border: '1.5px solid #86efac',
            borderRadius: 10,
            padding: '14px 16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, color: '#166534', fontWeight: 800, fontSize: 13.5 }}>
            <span style={{ fontSize: 16 }}>✅</span>
            <span>NÊN LÀM / CHUẨN ĐẠO ĐỨC & HIỆU QUẢ</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {dos.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#14532d', lineHeight: 1.5 }}>
                <span style={{ color: '#16a34a', fontWeight: 900, marginTop: -1 }}>✔</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* DONT column */}
        <div
          style={{
            background: '#fef2f2',
            border: '1.5px solid #fca5a5',
            borderRadius: 10,
            padding: '14px 16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, color: '#991b1b', fontWeight: 800, fontSize: 13.5 }}>
            <span style={{ fontSize: 16 }}>❌</span>
            <span>TUYỆT ĐỐI TRÁNH / VÙNG NGUY HIỂM</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {donts.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#7f1d1d', lineHeight: 1.5 }}>
                <span style={{ color: '#dc2626', fontWeight: 900, marginTop: -1 }}>✖</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Widget 11: Kim Tự Tháp 3 Lớp Nhu Cầu Khách Hàng
function VisualPyramidFlow({ layers, rawText }) {
  const [copied, setCopied] = useState(false);

  return (
    <div
      style={{
        margin: '22px 0',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '11px 18px',
          background: '#eff6ff',
          borderBottom: '1px solid #bfdbfe',
        }}
      >
        <span style={{ fontWeight: 800, fontSize: 13, color: '#1e40af' }}>
          🔺 MÔ HÌNH 3 LỚP NHU CẦU KHÁCH HÀNG TẠI QUẦY THUỐC
        </span>
        <Button
          size="small"
          type="text"
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={() => {
            navigator.clipboard.writeText(rawText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          style={{ fontSize: 11, color: '#1e40af' }}
        >
          {copied ? 'Đã chép' : 'Sao chép'}
        </Button>
      </div>

      <div style={{ padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {layers.map((layer, idx) => {
          const isTop = idx === 0;
          const isMiddle = idx === 1;

          const bg = isTop ? '#fef3c7' : isMiddle ? '#e0f2fe' : '#f0fdf4';
          const border = isTop ? '#f59e0b' : isMiddle ? '#0284c7' : '#16a34a';
          const textCol = isTop ? '#92400e' : isMiddle ? '#075985' : '#166534';
          const icon = isTop ? '👑' : isMiddle ? '🛡️' : '💊';
          const width = isTop ? '82%' : isMiddle ? '92%' : '100%';

          return (
            <div
              key={idx}
              style={{
                width,
                margin: '0 auto',
                padding: '12px 18px',
                borderRadius: 10,
                background: bg,
                border: `1.5px solid ${border}`,
                color: textCol,
                boxShadow: '0 2px 5px rgba(0,0,0,0.03)',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontWeight: 800, fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{icon}</span> {layer.title}
                </span>
                {layer.badge && (
                  <span style={{ padding: '2px 8px', borderRadius: 10, background: '#ffffff', fontSize: 11, fontWeight: 700, color: textCol, border: `1px solid ${border}` }}>
                    {layer.badge}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.5, opacity: 0.9 }}>
                {layer.desc}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Widget 12: Bộ 4 Chỉ Số Sống Còn (Infographic Cards)
function VisualMetricsGrid({ metrics, rawText }) {
  const [copied, setCopied] = useState(false);

  const colors = [
    { bg: '#eff6ff', border: '#93c5fd', text: '#1d4ed8', icon: '👥' },
    { bg: '#f0fdf4', border: '#86efac', text: '#15803d', icon: '📦' },
    { bg: '#fef3c7', border: '#fcd34d', text: '#b45309', icon: '💰' },
    { bg: '#fdf4ff', border: '#f0abfc', text: '#a21caf', icon: '🔄' },
  ];

  return (
    <div
      style={{
        margin: '22px 0',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '11px 18px',
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <span style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>
          📊 MA TRẬN 4 CHỈ SỐ SỐNG CÒN CỦA NHÀ THUỐC
        </span>
        <Button
          size="small"
          type="text"
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={() => {
            navigator.clipboard.writeText(rawText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          style={{ fontSize: 11, color: '#64748b' }}
        >
          {copied ? 'Đã chép' : 'Sao chép'}
        </Button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
          gap: 16,
          padding: '18px',
        }}
      >
        {metrics.map((m, i) => {
          const col = colors[i % colors.length];
          return (
            <div
              key={i}
              style={{
                background: col.bg,
                border: `1.5px solid ${col.border}`,
                borderRadius: 10,
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                minWidth: 0,
                boxSizing: 'border-box',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 20 }}>{col.icon}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: 10,
                    background: '#ffffff',
                    color: col.text,
                    border: `1px solid ${col.border}`,
                  }}
                >
                  Chỉ số #{i + 1}
                </span>
              </div>
              <div style={{ fontWeight: 800, fontSize: 14.5, color: col.text, lineHeight: 1.3 }}>
                {m.name}
              </div>
              {m.target && (
                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: '#334155',
                    background: '#ffffff',
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    gap: 4,
                  }}
                >
                  <span>🎯 Ngưỡng chuẩn:</span>
                  <span style={{ color: col.text }}>{m.target}</span>
                </div>
              )}
              {m.formula && (
                <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.5, wordBreak: 'break-word' }}>
                  <strong>Công thức:</strong> {m.formula}
                </div>
              )}
              {m.action && (
                <div
                  style={{
                    fontSize: 12,
                    color: '#475569',
                    lineHeight: 1.5,
                    borderTop: '1px dashed rgba(0,0,0,0.12)',
                    paddingTop: 8,
                    marginTop: 'auto',
                    wordBreak: 'break-word',
                  }}
                >
                  🛠️ <strong>Khi thấp:</strong> {m.action}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Widget 13: Thẻ Tóm Tắt Bài Học Cốt Lõi (Key Takeaway)
function VisualSummaryCard({ title, items, rawText }) {
  const [copied, setCopied] = useState(false);

  return (
    <div
      style={{
        margin: '24px 0',
        borderRadius: 12,
        border: '1.5px solid #86efac',
        background: '#f0fdf4',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 18px',
          borderBottom: '1px solid #bbf7d0',
          background: '#ffffff',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>🎯</span>
          <span style={{ fontWeight: 800, fontSize: 14, color: '#14532d', letterSpacing: 0.2 }}>
            {title || 'TỔNG KẾT BÀI HỌC — 3 PHÚT GHI NHỚ THỰC CHIẾN'}
          </span>
        </div>
        <Button
          size="small"
          type="text"
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={() => {
            navigator.clipboard.writeText(rawText);
            setCopied(true);
            toast.success('Đã sao chép tóm tắt bài học');
            setTimeout(() => setCopied(false), 2000);
          }}
          style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}
        >
          {copied ? 'Đã chép' : 'Sao chép'}
        </Button>
      </div>

      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((item, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              background: '#ffffff',
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid #bbf7d0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: '#16a34a',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 800,
                flexShrink: 0,
                marginTop: 1,
              }}
            >
              {idx + 1}
            </div>
            <div style={{ fontSize: 13, color: '#1f2937', lineHeight: 1.5, fontWeight: 500 }}>
              {item}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Widget 14: Timeline Quy Trình 6 Bước Chuẩn (Visual Timeline)
function VisualTimelineFlow({ steps, rawText }) {
  const [copied, setCopied] = useState(false);

  const stepColors = [
    { bg: '#e0f2fe', text: '#0284c7', border: '#bae6fd', badge: '#0369a1', icon: '👁️' },
    { bg: '#ede9fe', text: '#7c3aed', border: '#ddd6fe', badge: '#6d28d9', icon: '🔍' },
    { bg: '#cffafe', text: '#0891b2', border: '#a5f3fc', badge: '#0e7490', icon: '🤝' },
    { bg: '#dcfce7', text: '#16a34a', border: '#bbf7d0', badge: '#15803d', icon: '💡' },
    { bg: '#fef3c7', text: '#d97706', border: '#fde68a', badge: '#b45309', icon: '📋' },
    { bg: '#fce7f3', text: '#db2777', border: '#fbcfe8', badge: '#be185d', icon: '🔄' },
  ];

  return (
    <div
      style={{
        margin: '22px 0',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '11px 18px',
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <span style={{ fontWeight: 800, fontSize: 13, color: '#5b21b6' }}>
          🚀 SƠ ĐỒ TIMELINE QUY TRÌNH 6 BƯỚC TƯ VẤN CHUẨN TẠI QUẦY (3–4 PHÚT)
        </span>
        <Button
          size="small"
          type="text"
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={() => {
            navigator.clipboard.writeText(rawText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          style={{ fontSize: 11, color: '#7c3aed' }}
        >
          {copied ? 'Đã chép' : 'Sao chép'}
        </Button>
      </div>

      <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {steps.map((step, idx) => {
          const col = stepColors[idx % stepColors.length];
          return (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 14,
                padding: '12px 16px',
                borderRadius: 10,
                background: col.bg,
                border: `1.5px solid ${col.border}`,
                position: 'relative',
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#ffffff',
                  border: `2px solid ${col.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 15,
                  flexShrink: 0,
                }}
              >
                {col.icon}
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                  <span style={{ fontWeight: 800, fontSize: 14, color: col.text }}>
                    Bước {step.num || idx + 1}: {step.title}
                  </span>
                  {step.duration && (
                    <span style={{ padding: '2px 8px', borderRadius: 10, background: '#ffffff', fontSize: 11, fontWeight: 700, color: col.badge, border: `1px solid ${col.border}` }}>
                      ⏱️ {step.duration}
                    </span>
                  )}
                </div>
                {step.goal && (
                  <div style={{ fontSize: 12.5, color: '#334155', fontWeight: 600, marginBottom: 2 }}>
                    🎯 <strong>Mục tiêu:</strong> {step.goal}
                  </div>
                )}
                {step.desc && (
                  <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.45 }}>
                    {step.desc}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function MarkdownContent({ content, storageKey: customKey, isPractice = false }) {
  const safeContent = content || '';
  const computedKey = useMemo(() => simpleHash(safeContent), [safeContent]);
  const storageKey = customKey || computedKey;

  // State lưu trữ tương tác khi ở chế độ thực hành (isPractice = true)
  const [interactiveState, setInteractiveState] = useState(() => {
    if (!isPractice) return {};
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const counterRef = useRef(0);
  counterRef.current = 0;

  useEffect(() => {
    if (!isPractice) return;
    try {
      const saved = localStorage.getItem(storageKey);
      setInteractiveState(saved ? JSON.parse(saved) : {});
    } catch {
      setInteractiveState({});
    }
  }, [storageKey, isPractice]);

  if (!content) {
    return <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>Không có nội dung văn bản.</div>;
  }

  const handleUpdateField = (key, value) => {
    setInteractiveState((prev) => {
      const next = { ...prev, [key]: value };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  const handleReset = () => {
    setInteractiveState({});
    localStorage.removeItem(storageKey);
    toast.info('Đã xóa dữ liệu để bạn thực hành đánh dấu & điền lại từ đầu.');
  };

  // Helper duyệt và chuyển đổi chuỗi text (CHỈ áp dụng cho phần bài tập thực hành isPractice = true)
  const transformInteractiveNodes = (children) => {
    if (!children) return children;

    // Nếu là phần đọc tài liệu / bài giảng (isPractice = false):
    // Giữ nguyên dạng text tự nhiên của tài liệu, KHÔNG biến thành ô input hay checkbox
    if (!isPractice) {
      return children;
    }

    if (typeof children === 'string') {
      const masterRegex =
        /((?:🔶\s*)?[☐_]{2}\/[☐_]{2}\/[☐_]{4}|(?:☐\s*[^\n\r☐|]{1,25}){2,}|[☐_]{1,6}\s*(?:giây|phút|giờ|thao tác|món|đơn|mã|triệu|đ|đồng|%|\/\s*\d+)|☐\s*[._]{3,}|[☐]{2,}|[☐☑]|[._]{4,}|🔶)/gu;

      if (!masterRegex.test(children)) {
        return children;
      }

      const parts = children.split(masterRegex);
      return parts.map((part, i) => {
        if (!part) return null;

        // 1. Nhận diện Ngày tháng (VD: 🔶☐☐/☐☐/☐☐☐☐ hoặc ☐☐/☐☐/☐☐☐☐)
        if (/(?:🔶\s*)?[☐_]{2}\/[☐_]{2}\/[☐_]{4}/.test(part)) {
          const idx = ++counterRef.current;
          const key = `date_${idx}`;
          const isOrange = part.includes('🔶');
          return (
            <InteractiveDateInput
              key={`date_${idx}_${i}`}
              value={interactiveState[key]}
              onChange={(val) => handleUpdateField(key, val)}
              isOrange={isOrange}
            />
          );
        }

        // 2. Nhận diện Nhóm lựa chọn nhiều phương án (VD: ☐ Xuất được ☐ Không xuất)
        if (/(?:☐\s*[^\n\r☐|]{1,25}){2,}/u.test(part)) {
          const rawOptions = part
            .split('☐')
            .map((o) => o.trim())
            .filter(Boolean);
          if (rawOptions.length >= 2) {
            const idx = ++counterRef.current;
            const key = `choice_${idx}`;
            return (
              <InteractiveChoiceGroup
                key={`choice_${idx}_${i}`}
                options={rawOptions}
                selectedValue={interactiveState[key]}
                onSelect={(val) => handleUpdateField(key, val)}
              />
            );
          }
        }

        // 3. Nhận diện Chỉ số / đơn vị (VD: ☐ giây, ______ %, ☐ / 7)
        const metricMatch = part.match(
          /^([☐_]{1,6})\s*(giây|phút|giờ|thao tác|món|đơn|mã|triệu|đ|đồng|%|\/\s*\d+)$/u
        );
        if (metricMatch) {
          const idx = ++counterRef.current;
          const key = `metric_${idx}`;
          const unit = metricMatch[2];
          return (
            <InteractiveMetricInput
              key={`metric_${idx}_${i}`}
              value={interactiveState[key]}
              unit={unit}
              onChange={(val) => handleUpdateField(key, val)}
            />
          );
        }

        // 4. Nhận diện Ô vuông kèm dòng điền bài tập (VD: ☐ ....................................)
        if (/^☐\s*[._]{3,}$/.test(part)) {
          const idx = ++counterRef.current;
          const checkKey = `chk_${idx}`;
          const inputKey = `txt_${idx}`;
          const isChecked = !!interactiveState[checkKey];
          const textVal = interactiveState[inputKey] || '';

          return (
            <span
              key={`cb_in_${idx}_${i}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                margin: '4px 0',
                width: '100%',
                maxWidth: 680,
              }}
            >
              <InteractiveCheckboxToggle
                isChecked={isChecked}
                onToggle={() => handleUpdateField(checkKey, !isChecked)}
              />
              <input
                type="text"
                value={textVal}
                placeholder="Nhập câu trả lời / viết lại bằng lời của bạn tại đây..."
                onChange={(e) => handleUpdateField(inputKey, e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 12px',
                  border: '1px solid #cbd5e1',
                  borderBottom: isChecked ? '2px solid #16a34a' : '2px solid #0284c7',
                  borderRadius: 6,
                  fontSize: 13,
                  background: isChecked ? '#f0fdf4' : '#ffffff',
                  outline: 'none',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.03)',
                  transition: 'all 0.2s',
                }}
              />
            </span>
          );
        }

        // 5. Nhận diện Chuỗi ô vuông điền tên/mã (VD: Nhà thuốc: ☐☐☐☐☐☐)
        if (/^[☐]{2,}$/.test(part)) {
          const idx = ++counterRef.current;
          const key = `name_${idx}`;
          const boxCount = part.length;
          const width = Math.min(240, Math.max(90, boxCount * 18));
          return (
            <input
              key={`name_${idx}_${i}`}
              type="text"
              value={interactiveState[key] || ''}
              placeholder="Nhập thông tin..."
              onChange={(e) => handleUpdateField(key, e.target.value)}
              style={{
                width,
                padding: '3px 8px',
                margin: '0 4px',
                border: 'none',
                borderBottom: '2px solid #0284c7',
                background: '#f8fafc',
                fontSize: 13,
                fontWeight: 600,
                color: '#0f172a',
                outline: 'none',
                verticalAlign: 'baseline',
                borderRadius: '3px 3px 0 0',
              }}
            />
          );
        }

        // 6. Nhận diện Ô vuông lẻ (☐ hoặc ☑)
        if (part === '☐' || part === '☑') {
          const idx = ++counterRef.current;
          const key = `chk_${idx}`;
          const isChecked =
            interactiveState[key] !== undefined ? !!interactiveState[key] : part === '☑';

          return (
            <InteractiveCheckboxToggle
              key={`chk_${idx}_${i}`}
              isChecked={isChecked}
              onToggle={() => handleUpdateField(key, !isChecked)}
            />
          );
        }

        // 7. Nhận diện Dòng chấm / gạch điền (VD: ................. hoặc ________________)
        if (/^[._]{4,}$/.test(part)) {
          const idx = ++counterRef.current;
          const key = `blank_${idx}`;
          return (
            <input
              key={`blank_${idx}_${i}`}
              type="text"
              value={interactiveState[key] || ''}
              placeholder="Điền vào đây..."
              onChange={(e) => handleUpdateField(key, e.target.value)}
              style={{
                display: 'inline-block',
                minWidth: 110,
                maxWidth: 240,
                padding: '3px 8px',
                margin: '0 4px',
                border: 'none',
                borderBottom: '2px solid #0284c7',
                background: '#f8fafc',
                fontSize: 13,
                fontWeight: 600,
                color: '#0f172a',
                outline: 'none',
                verticalAlign: 'baseline',
                borderRadius: '3px 3px 0 0',
              }}
            />
          );
        }

        // 8. Nhận diện Huy hiệu 🔶
        if (part === '🔶') {
          return (
            <Tag
              key={`amber_${i}`}
              color="warning"
              style={{ fontSize: 11, fontWeight: 700, margin: '0 2px', verticalAlign: 'middle' }}
            >
              🔶 Cần điền / Rà soát
            </Tag>
          );
        }

        return part;
      });
    }

    if (Array.isArray(children)) {
      return React.Children.map(children, (child) => transformInteractiveNodes(child));
    }

    if (React.isValidElement(children) && children.props && children.props.children) {
      return React.cloneElement(children, {
        children: transformInteractiveNodes(children.props.children),
      });
    }

    return children;
  };

  const components = {
    // Custom Code Block & Pre Renderer (Tất cả sơ đồ, kịch bản, kim tự tháp, checklist trực quan)
    code: ({ node, inline, className, children, ...props }) => {
      const codeText = String(children || '').trim();

      if (
        inline ||
        (!codeText.includes('\n') &&
          codeText.length < 50 &&
          !codeText.includes('①') &&
          !codeText.includes('➀') &&
          !codeText.includes('[') &&
          !className)
      ) {
        return (
          <code
            className={className}
            {...props}
            style={{
              padding: '2px 6px',
              borderRadius: 4,
              background: '#f1f5f9',
              color: '#0f172a',
              fontWeight: 600,
              fontSize: '0.9em',
            }}
          >
            {children}
          </code>
        );
      }

      const lang = (className || '').replace('language-', '').toLowerCase();
      const lines = codeText.split('\n');
      const nonBlankLines = lines.map((l) => l.trim()).filter(Boolean);

      // A. Nhận diện Kịch bản Chat / Hội thoại Roleplay
      if (
        lang === 'chat' ||
        lang === 'dialog' ||
        lang === 'roleplay' ||
        lines.some((l) => /^\[(dược sĩ|khách hàng|ds|kh|người mua|nhân viên|bạn|lưu ý|ghi chú)\]/i.test(l.trim()))
      ) {
        const messages = [];
        lines.forEach((l) => {
          const t = l.trim();
          if (!t) return;
          const match = t.match(/^\[(.*?)\]\s*(?::|—|-)?\s*(.*)/) || t.match(/^([^:]+):\s*(.*)/);
          if (match) {
            messages.push({
              speaker: match[1].trim(),
              text: match[2].trim(),
            });
          } else {
            messages.push({
              speaker: 'Ghi chú',
              text: t,
            });
          }
        });
        if (messages.length > 0) {
          return <VisualChatFlow messages={messages} rawText={codeText} />;
        }
      }

      // B. Nhận diện So sánh ĐÚNG vs SAI (Do's & Don'ts)
      if (
        lang === 'dos-donts' ||
        lang === 'comparison' ||
        (codeText.includes('[DO]') && codeText.includes('[DONT]')) ||
        (codeText.includes('[NÊN LÀM]') && codeText.includes('[TRÁNH]'))
      ) {
        const dos = [];
        const donts = [];
        let currentMode = 'do';
        lines.forEach((l) => {
          const t = l.trim();
          if (!t) return;
          if (/^\[(DO|NÊN LÀM|NÊN)\]/i.test(t)) {
            currentMode = 'do';
            return;
          }
          if (/^\[(DONT|DON'T|KHÔNG NÊN|TRÁNH|TUYỆT ĐỐI TRÁNH)\]/i.test(t)) {
            currentMode = 'dont';
            return;
          }
          const cleaned = t.replace(/^[-*•✔✖xX✓]\s*/, '').trim();
          if (cleaned) {
            if (currentMode === 'do') dos.push(cleaned);
            else donts.push(cleaned);
          }
        });
        if (dos.length > 0 || donts.length > 0) {
          return <VisualDosDontsFlow dos={dos} donts={donts} rawText={codeText} />;
        }
      }

      // C. Nhận diện Kim tự tháp / Phân cấp nhu cầu
      if (
        lang === 'pyramid' ||
        lang === 'layers' ||
        lines.some((l) => /^\[(đỉnh|giữa|đáy|tầng 1|tầng 2|tầng 3|lớp 1|lớp 2|lớp 3)\]/i.test(l.trim()))
      ) {
        const layers = [];
        lines.forEach((l) => {
          const t = l.trim();
          if (!t) return;
          const match = t.match(/^\[(.*?)\]\s*(?::|—|-)?\s*([^:(]+)(?:\((.*)\))?/);
          if (match) {
            layers.push({
              badge: match[1].trim(),
              title: match[2].trim(),
              desc: match[3]?.trim() || '',
            });
          } else {
            layers.push({
              title: t,
            });
          }
        });
        if (layers.length > 0) {
          return <VisualPyramidFlow layers={layers} rawText={codeText} />;
        }
      }

      // D. Nhận diện Ma trận 4 Chỉ số KPI
      if (
        lang === 'metrics' ||
        lang === 'kpi' ||
        (codeText.includes('LƯỢT KHÁCH') && codeText.includes('SỐ MÓN'))
      ) {
        const metrics = [];
        lines.forEach((l) => {
          const t = l.trim();
          if (!t || t.startsWith('---') || t.startsWith('===')) return;
          const parts = t.split('|').map((p) => p.trim()).filter(Boolean);
          if (parts.length >= 2 && !parts[0].includes('#') && !parts[0].includes('Chỉ số')) {
            metrics.push({
              name: parts[1] || parts[0],
              target: parts[3] || parts[2] || '',
              formula: parts[2] || '',
              action: parts[4] || '',
            });
          }
        });
        if (metrics.length >= 2) {
          return <VisualMetricsGrid metrics={metrics} rawText={codeText} />;
        }
      }

      // E. Nhận diện Tóm tắt bài học cốt lõi
      if (
        lang === 'summary' ||
        lang === 'takeaway' ||
        (codeText.includes('🎯') && codeText.includes('CỐT LÕI'))
      ) {
        let title = 'TỔNG KẾT BÀI HỌC — 3 PHÚT GHI NHỚ THỰC CHIẾN';
        const items = [];
        lines.forEach((l) => {
          const t = l.trim();
          if (!t) return;
          if (t.includes('🎯') || t.includes('TỔNG KẾT') || t.includes('CỐT LÕI')) {
            title = t.replace(/^[#\-*🎯\s]+/, '').trim();
            return;
          }
          const cleaned = t.replace(/^\d+[\s.)-]+/, '').replace(/^[-*•]\s*/, '').trim();
          if (cleaned) items.push(cleaned);
        });
        if (items.length > 0) {
          return <VisualSummaryCard title={title} items={items} rawText={codeText} />;
        }
      }

      // F. Nhận diện Timeline quy trình các bước
      if (
        lang === 'timeline' ||
        lang === 'steps' ||
        lines.some((l) => /^\[?bước\s*\d+\]?/i.test(l.trim()))
      ) {
        const steps = [];
        lines.forEach((l) => {
          const t = l.trim();
          if (!t) return;
          const match = t.match(/^\[?bước\s*(\d+)\]?\s*(?::|—|-)?\s*(.*?)(?:\((.*?)\))?(?:\s*—\s*(.*))?$/i);
          if (match) {
            steps.push({
              num: match[1],
              title: match[2]?.trim() || '',
              duration: match[3]?.trim() || '',
              goal: match[4]?.trim() || '',
            });
          } else {
            const mStep = t.match(/^([①②③④⑤⑥⑦⑧⑨⑩➀➁➂➃➄➅➆➇➈➉]|\d+[.)])\s*(.*?)(?:\((.*?)\))?(?:\s*—\s*(.*))?$/);
            if (mStep) {
              steps.push({
                num: mStep[1],
                title: mStep[2]?.trim() || '',
                duration: mStep[3]?.trim() || '',
                goal: mStep[4]?.trim() || '',
              });
            }
          }
        });
        if (steps.length >= 2) {
          return <VisualTimelineFlow steps={steps} rawText={codeText} />;
        }
      }

      // G. Nhận diện Sơ đồ Phễu bán hàng / Cây phân nhánh
      const hasTreeChars = lines.some((l) => /[├└]─|\(1\)/.test(l));
      if (hasTreeChars) {
        let root = '';
        const nodes = [];
        lines.forEach((l) => {
          const trimmed = l.trim();
          if (!trimmed || trimmed === '│' || trimmed === '|') return;

          const treeMatch = trimmed.match(/^[├└]─*\s*(?:\((\d+)\)|\d+[.)])\s*(.+)/);
          if (treeMatch) {
            const stepNo = treeMatch[1];
            const rest = treeMatch[2];
            const split = rest.split(/\.{2,}|…+|\s{2,}|—/);
            nodes.push({
              stepNo: parseInt(stepNo, 10),
              title: split[0]?.trim() || '',
              desc: split.slice(1).join(' — ').trim() || '',
            });
          } else if (!root && !trimmed.includes('├') && !trimmed.includes('└')) {
            root = trimmed;
          }
        });

        if (nodes.length > 0) {
          return <VisualFunnelFlow root={root} nodes={nodes} rawText={codeText} />;
        }
      }

      // H. Nhận diện Sơ đồ quy trình dạng các bước tròn (① ② ③)
      const stepLines = nonBlankLines.filter((l) => /^[①②③④⑤⑥⑦⑧⑨⑩➀➁➂➃➄➅➆➇➈➉]/.test(l));
      if (stepLines.length >= 2) {
        const steps = [];
        const numMap = {
          '①': '1', '②': '2', '③': '3', '④': '4', '⑤': '5',
          '⑥': '6', '⑦': '7', '⑧': '8', '⑨': '9', '⑩': '10',
          '➀': '1', '➁': '2', '➂': '3', '➃': '4', '➄': '5',
          '➅': '6', '➆': '7', '➇': '8', '➈': '9', '➉': '10',
        };

        nonBlankLines.forEach((l) => {
          if (l.includes('↕') || l.includes('↓')) {
            steps.push({
              isConnector: true,
              text: l.replace(/^[↕↓\s]+/, '').trim() || 'phải khớp đồng bộ',
            });
          } else {
            const m = l.match(/^([①②③④⑤⑥⑦⑧⑨⑩➀➁➂➃➄➅➆➇➈➉])\s*(.*)/);
            if (m) {
              const numChar = m[1];
              const rest = m[2];
              const split = rest.split(/\s{2,}|\s*—\s*|\s*:\s*/);
              steps.push({
                num: numMap[numChar] || numChar,
                title: split[0] || '',
                desc: split.slice(1).join(' — ') || '',
              });
            }
          }
        });

        if (steps.length > 0) {
          return <VisualProcessFlow steps={steps} rawText={codeText} />;
        }
      }

      // I. Nhận diện công thức ghép nối (VD: [NỐI VẤN ĐỀ] + [ĐỀ XUẤT] + [LÝ DO])
      if (codeText.includes('+') && codeText.includes('[') && codeText.includes(']')) {
        const formulaParts = codeText
          .split('+')
          .map((p) => p.replace(/[[\]]/g, '').trim())
          .filter(Boolean);
        if (formulaParts.length >= 2) {
          return <VisualFormulaFlow formulaParts={formulaParts} rawText={codeText} />;
        }
      }

      // J. Mặc định khung code pastel
      return <VisualPromptCard rawText={codeText} />;
    },

    pre: ({ node, children, ...props }) => {
      return (
        <div style={{ margin: '16px 0', whiteSpace: 'normal', minWidth: 0, wordBreak: 'break-word' }}>
          {children}
        </div>
      );
    },

    blockquote: ({ node, children, ...props }) => {
      return (
        <blockquote
          {...props}
          style={{
            margin: '20px 0',
            padding: '14px 18px',
            background: '#f0fdf4',
            borderLeft: '4px solid #005537',
            borderRadius: '0 8px 8px 0',
            color: '#1e293b',
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.02)',
          }}
        >
          {transformInteractiveNodes(children)}
        </blockquote>
      );
    },

    img: ({ node, src, alt, ...props }) => {
      return (
        <figure style={{ margin: '24px 0', textAlign: 'center' }}>
          <img
            src={src}
            alt={alt || 'Hình minh họa bài học'}
            style={{
              maxWidth: '100%',
              maxHeight: 520,
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
              display: 'inline-block',
              objectFit: 'contain',
            }}
            loading="lazy"
            {...props}
          />
          {alt && (
            <figcaption style={{ fontSize: 12.5, color: '#64748b', marginTop: 8, fontStyle: 'italic' }}>
              📷 {alt}
            </figcaption>
          )}
        </figure>
      );
    },

    h2: ({ node, children, ...props }) => {
      const headingText = String(children || '').trim();
      let partId = '';
      const partMatch = headingText.match(/phần\s*(\d+)/i);
      if (partMatch) {
        partId = `part-${partMatch[1]}`;
      }
      return (
        <h2
          id={partId || props.id}
          {...props}
          style={{
            scrollMarginTop: '80px',
            marginTop: '32px',
            marginBottom: '16px',
            fontWeight: 800,
            color: '#0f172a',
            borderBottom: '1px solid #f1f5f9',
            paddingBottom: '8px',
          }}
        >
          {transformInteractiveNodes(children)}
        </h2>
      );
    },
    h3: ({ node, children, ...props }) => {
      const headingText = String(children || '').trim();
      let partId = '';
      const partMatch = headingText.match(/phần\s*(\d+)/i);
      if (partMatch) {
        partId = `part-${partMatch[1]}`;
      }
      return (
        <h3
          id={partId || props.id}
          {...props}
          style={{
            scrollMarginTop: '80px',
            marginTop: '24px',
            marginBottom: '12px',
            fontWeight: 700,
            color: '#1e293b',
          }}
        >
          {transformInteractiveNodes(children)}
        </h3>
      );
    },
    input: ({ node, ...props }) => {
      if (props.type === 'checkbox' && isPractice) {
        const idx = ++counterRef.current;
        const key = `gfm_chk_${idx}`;
        const isChecked =
          interactiveState[key] !== undefined ? !!interactiveState[key] : !!props.checked;

        return (
          <InteractiveCheckboxToggle
            isChecked={isChecked}
            onToggle={() => handleUpdateField(key, !isChecked)}
          />
        );
      }
      return <input {...props} />;
    },
    p: ({ node, children, ...props }) => {
      return <p {...props}>{transformInteractiveNodes(children)}</p>;
    },
    li: ({ node, children, ...props }) => {
      return <li {...props}>{transformInteractiveNodes(children)}</li>;
    },
    // Bảng nhiều cột cuộn ngang trong khung riêng, không làm tràn trang trên điện thoại
    table: ({ node, ...props }) => (
      <div className="markdown-table-scroll">
        <table {...props} />
      </div>
    ),
    td: ({ node, children, ...props }) => {
      const rawText = String(children || '').trim();

      // CHỈ khi ở chế độ bài tập thực hành (isPractice = true) mới biến ô bảng thành Input
      if (isPractice && (rawText === '☐' || rawText === '')) {
        const idx = ++counterRef.current;
        const cellKey = `tbl_cell_${idx}`;
        const cellValue = interactiveState[cellKey] || '';

        return (
          <td
            {...props}
            style={{
              verticalAlign: 'middle',
              padding: '8px 10px',
            }}
          >
            <input
              type="text"
              value={cellValue}
              placeholder="Nhập nội dung vào đây..."
              onChange={(e) => handleUpdateField(cellKey, e.target.value)}
              style={{
                width: '100%',
                minWidth: 130,
                padding: '6px 12px',
                border: '1px solid #cbd5e1',
                borderRadius: 6,
                background: cellValue ? '#f0fdf4' : '#ffffff',
                fontSize: 13,
                color: '#0f172a',
                outline: 'none',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
                transition: 'all 0.2s',
              }}
            />
          </td>
        );
      }

      return (
        <td
          {...props}
          style={{
            verticalAlign: 'middle',
            padding: '10px 12px',
          }}
        >
          {transformInteractiveNodes(children)}
        </td>
      );
    },
    th: ({ node, children, ...props }) => {
      return (
        <th
          {...props}
          style={{
            background: '#f8fafc',
            color: '#1e293b',
            fontWeight: 700,
            padding: '12px',
            borderBottom: '2px solid #e2e8f0',
          }}
        >
          {transformInteractiveNodes(children)}
        </th>
      );
    },
  };

  const totalInteractions = Object.keys(interactiveState).length;

  return (
    <div>
      {/* Banner trợ giúp thực hành CHỈ hiển thị ở phần bài tập thực hành (isPractice = true) */}
      {isPractice && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 10,
            padding: '10px 16px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            marginBottom: 20,
            fontSize: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <CheckSquareOutlined style={{ color: '#0284c7', fontSize: 16 }} />
            <span style={{ fontWeight: 600, color: '#334155' }}>
              Khung thực hành tương tác: Hỗ trợ tick chọn checklist, điền trực tiếp vào bảng bài tập và tự động lưu tiến trình.
            </span>
            {totalInteractions > 0 && (
              <Tag color="green" icon={<CheckCircleOutlined />}>
                Đã lưu {totalInteractions} mục thực hành
              </Tag>
            )}
          </div>

          {totalInteractions > 0 && (
            <Button
              size="small"
              type="text"
              icon={<ReloadOutlined />}
              onClick={handleReset}
              style={{ fontSize: 12, color: '#64748b' }}
            >
              Làm lại từ đầu
            </Button>
          )}
        </div>
      )}

      {/* Render Markdown Content */}
      <div className="markdown-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {content}
        </ReactMarkdown>
      </div>
    </div>
  );
}
